import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {HOTB} from "../HOTB";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {HOTBData, HOTBGameData, HOTBInitData} from "../server/HOTBData";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {SymbolsList} from "../reels/GameSymbol";
import {ExtendedGameMode} from "../server/ExtendedGameMode";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {BetFeature} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/bethandler/BetFeatureController";
import {ExtendedGameModel} from "../server/ExtendedGameModel";

export class GameUtils {
    private static prevUniqueNum: number;

    private static _buyFeatureName: string = "";

    static get buyFeatureName(): string {
        return this._buyFeatureName;
    }

    static set buyFeatureName(value: string) {
        this._buyFeatureName = value;
    }

    public static isAllTrue(array: boolean[]): boolean {
        for (let value of array) {
            if (!value) {
                return false;
            }
        }
        return true;
    }

    public static isBelowWinRestrictedJU(): boolean {
        return HOTB.apiPlugin.gameClientConfiguration.belowStakeWinRestriction;
    }

    public static isSingleWinBelowStake(): boolean {
        let flag: boolean = false;

        if (this.isBelowWinRestrictedJU()) {
            const gameModel: ExtendedGameModel = HOTB.model,
                hotbData: HOTBData = gameModel.data;

            let featurePrice: number = this.getBuyFeaturePrice(),
                priceToConsider: number = featurePrice;

            if (hotbData.mode.includes(ExtendedGameMode.FREESPIN)) {
                priceToConsider = hotbData.playedBetValue;
            }

            if (priceToConsider >= HOTB.model.gameData.spinRoundWin && hotbData.accumulatedRoundWin <= featurePrice) {
                flag = true;
            }
        }

        return flag;
    }

    public static isWinBelowStake(): boolean {
        let flag: boolean = false;

        if (this.isBelowWinRestrictedJU()) {
            const featurePrice: number = this.getBuyFeaturePrice();

            if (HOTB.model.data.accumulatedRoundWin <= featurePrice) {
                flag = true;
            }
        }

        return flag;
    }

    public static getTimeStamp(): string {
        const today: Date = new Date();
        return today.getHours() + ":" + today.getMinutes() + ":" + today.getSeconds() + ":" + today.getMilliseconds();
    }

    public static getMaxWinLength(data: IBetWayWinData[]): number {
        return Math.max(...data.map(item => item.length));
    }

    public static checkForEqualArrays(arr1: any[], arr2: any[]): boolean {
        if (!arr1 || !arr2) {
            return false;
        }
        if (arr1.length !== arr2.length) {
            return false;
        }

        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) {
                return false;
            }
        }

        return true;
    }

    public static getTotalMultiplier(reelId: number): number {
        const gameData: HOTBGameData = HOTB.model.gameData;
        let multValue: number = 0;

        for (let i: number = 0; i <= reelId; i++) {
            multValue += gameData.nudgeMultipliersNext[i];
        }

        return multValue;
    }

    public static hasSameValues(arr: any[], value: any): boolean {
        return arr.every(element => element === value);
    }

    public static getLayer(name: string): StageLayer | undefined {
        return NolimitApplication.getLayerByName(name);
    }

    public static isWild(symName: string): boolean {
        let flag: boolean = false;

        if (symName === SymbolsList.WILD || symName === SymbolsList.LAUNCH || symName === SymbolsList.LAUNCH_WILD || symName === SymbolsList.XBOMB || symName === SymbolsList.XNUDGE) {
            flag = true;
        }

        return flag;
    }

    public static toggleSymbols(show: boolean): void {
        HOTB.reelArea.toggleDisplay(show);
        if (show) {
            HOTB.topReel.show();
            HOTB.leftReel.show();
            HOTB.rightReel.show();
            HOTB.waysCounter.show();
            HOTB.gameMultiplier.show();
        } else {
            HOTB.topReel.hide();
            HOTB.leftReel.hide();
            HOTB.rightReel.hide();
            HOTB.waysCounter.hide();
            HOTB.gameMultiplier.hide();
        }
    }

    public static getGamePrefix(mode: ExtendedGameMode): string {
        let prefixText: string;

        if (mode === ExtendedGameMode.CAPWIN_GAME) {
            const gameData: HOTBGameData = HOTB.model.gameData;
            if (gameData && gameData.nextMode === ExtendedGameMode.NORMAL && gameData.numberOfFreespinsPlayed > 0 && gameData.freespinsLeft === 0) {
                const bgState: string = HOTB.gameBackground.getCurrentAnimation();
                if (bgState) {
                    const allModes: string[] = bgState.split("_"),
                        prevMode: string = allModes[0];

                    if (prevMode === "fs") {
                        mode = ExtendedGameMode.FREESPIN;
                    } else if (prevMode === "sfs") {
                        mode = ExtendedGameMode.FREESPIN_SUPER;
                    } else {
                        mode = allModes[0];
                    }
                } else {
                    mode = HOTB.model.data.mode;
                }
            } else {
                mode = HOTB.model.data.mode;
            }
        }

        switch (mode) {
            case ExtendedGameMode.FREESPIN:
            case ExtendedGameMode.FREESPIN_AVALANCHE:
                prefixText = "fs";
                break;
            case ExtendedGameMode.FREESPIN_SUPER:
            case ExtendedGameMode.FREESPIN_SUPER_AVALANCHE:
                prefixText = "sfs";
                break;
            default:
                prefixText = "mg";
                break;
        }

        return prefixText;
    }

    public static isFastSpin(): boolean {
        return HOTB.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
    }

    public static isFreeSpinTriggered(mode: string, nextMode: string): boolean {
        return (mode === ExtendedGameMode.NORMAL || mode === ExtendedGameMode.NORMAL_AVALANCHE) && (nextMode == ExtendedGameMode.FREESPIN || nextMode == ExtendedGameMode.FREESPIN_SUPER);
    }

    public static getBuyFeaturePrice(): number {
        const gameApi: ApiPlugin = HOTB.apiPlugin,
            betLevel: string = gameApi.freeBets.hasFreeBets() ? gameApi.freeBets.getBet() : gameApi.betLevel.getLevel(),
            gameModel: ExtendedGameModel = HOTB.model,
            initData: HOTBInitData = gameModel.initData,
            gameData: HOTBGameData = gameModel.gameData;

        let price: number = gameData?.playedBetValue || parseFloat(betLevel);
        this.buyFeatureName = HOTB.model.data.featureBuyName;

        for (let index: number = 0; index < initData.featureBuyTimesBetValue.length; index++) {
            if (initData.featureBuyTimesBetValue[index].name === this.buyFeatureName) {
                price *= initData.featureBuyTimesBetValue[index].price;
                break;
            }
        }

        return MathHelper.ceilToDecimals(price, 2);
    }

    public static isBoostedBetAllowed(): boolean {
        return HOTB.apiPlugin.gameClientConfiguration.boostedBetAllowed;
    }

    public static getActiveBetFeature(): BetFeature | undefined {
        return HOTB.apiPlugin.betFeatureController.getActiveBetFeature();
    }

    public static isSatanSpin(): boolean {
        const activeBetFeature: BetFeature | undefined = this.getActiveBetFeature();

        return activeBetFeature?.name === "SATAN_SPINS";
    }

    public static isCapMachineGame(): boolean {
        const activeBetFeature: BetFeature | undefined = this.getActiveBetFeature();

        return activeBetFeature?.name === ExtendedGameMode.CAPWIN_GAME;
    }

    public static isBigWin(): boolean {
        const gameModel: ExtendedGameModel = HOTB.model,
            hotbData: HOTBData = gameModel.data,
            nextMode: string = hotbData.nextMode,
            gameData: HOTBGameData = gameModel.gameData,
            brokeTheBank: boolean = gameData.brokeTheBank;

        let flag: boolean = false;

        if (gameModel.winRatio >= 15 && !brokeTheBank && !GameUtils.isSingleWinBelowStake()) {
            if (nextMode === ExtendedGameMode.NORMAL ||
                nextMode === ExtendedGameMode.FREESPIN ||
                nextMode === ExtendedGameMode.FREESPIN_SUPER) {
                flag = true;
            }
        }

        return flag;
    }

    public static uniqueNumberInRange(min: number, max: number, decimals: number = 0): number {
        if ((min === max) || (min > max)) { //min > max condition added because of blank array and it's length - 1
            return min;
        } else {
            let randomNumber: number = Number((min + (max - min) * Math.random()).toFixed(decimals));
            if (this.prevUniqueNum === randomNumber) {
                randomNumber = this.uniqueNumberInRange(min, max, decimals);
            }
            this.prevUniqueNum = randomNumber;
            return randomNumber;
        }
    }

    public static getRandomBoolean(whatPercentage: number): boolean {
        return Math.random() < (whatPercentage / 100);
    }
}